/* eslint-disable max-lines */
import { Text, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Stack, ThemeProvider } from "@mui/material";
import { useGetClipboardScoreHistory } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useDiscardLatenessBooktimeThreshold } from "@src/appV2/ClipboardScore/hooks/featureFlags";
import { useFacilityNotes } from "@src/appV2/Facilities/api/useFacilityNotes";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getValueInUsd } from "@src/appV2/lib/Money/utils/currency";
import { useDelayedReviewStorage } from "@src/appV2/PostShiftReview/DelayedReview/useDelayedReviewStorage";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import {
  useGetPayoutSettings,
  type UseGetPayoutSettingsResponse,
} from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetPayoutSettings";
import { PayoutSettingsPage } from "@src/appV2/redesign/Accounts/PayoutSettings/Page";
import { useArrivalTimeConfig } from "@src/appV2/redesign/ArrivalTime/useArrivalTimeConfig";
import { RestrictedBottomSheet } from "@src/appV2/redesign/ClipboardScore/components/BottomSheets/RestrictedBottomSheet";
import { useEmergencyCreditsClockOutFlow } from "@src/appV2/redesign/EmergencyCredits/hooks/useEmergencyCreditsClockOutFlow";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "@src/appV2/redesign/Shift/constants";
import { useGetAssignmentByShiftId } from "@src/appV2/redesign/Ten99Policy/api/useGetAssignmentByShiftId";
import { formatTime } from "@src/appV2/redesign/utils/formatTime";
import { TimesheetUploadSection } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/components/TimesheetUploadSection";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useGetShiftFeedbackClockOutConfig } from "@src/appV2/Shifts/Shift/useGetShiftFeedbackClockOutConfig";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addHours, addMinutes, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import { LoadingButton } from "../../../components/LoadingButton";
import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import {
  type Duration,
  durationToMinutes,
  minutesToDuration,
} from "../../../DateTime/DurationInput";
import { PageContent } from "../../components/PageContent";
import {
  MY_SHIFTS_FULL_PATH,
  SHIFT_DETAILS_EC_CLOCK_OUT_FLOW_PATH,
  SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH,
} from "../../path";
import { IncompleteBreakConfirmationDialog } from "../../Timekeeping/mandatoryBreakDialogs/IncompleteBreakConfirmationDialog/IncompleteBreakConfirmationDialog";
import { WorkedThroughMandatoryBreakExplanationDialog } from "../../Timekeeping/mandatoryBreakDialogs/WorkedThroughMandatoryBreakExplanationDialog/WorkedThroughMandatoryBreakExplanationDialog";
import { useIsTimekeepingIntegrationsEnabled } from "../../Timekeeping/TimekeepingIntegrations/useIsTimekeepingIntegrationsEnabled";
import {
  FacilitySignatureSection,
  type FacilitySignatureSectionRef,
} from "../../Timekeeping/Timesheet/components/FacilitySignatureSection";
import { QrSignatureSection } from "../../Timekeeping/Timesheet/components/QrSignatureSection";
import { ReadOnlyTimesheetTimeEntries } from "../../Timekeeping/Timesheet/components/ReadOnlyTimesheetTimeEntries";
import { TimesheetTimeEntries } from "../../Timekeeping/Timesheet/components/TimesheetTimeEntries";
import { WorkerSignatureSection } from "../../Timekeeping/Timesheet/components/WorkerSignatureSection";
import { EditBreakTimeBottomSheet } from "../../Timekeeping/Timesheet/EditBreakTimeBottomSheet";
import { EditClockInTimeBottomSheet } from "../../Timekeeping/Timesheet/EditClockInTimeBottomSheet";
import { EditClockOutTimeBottomSheet } from "../../Timekeeping/Timesheet/EditClockOutTimeBottomSheet";
import {
  type TimesheetEntry,
  useTimesheetEntry,
} from "../../Timekeeping/Timesheet/hooks/useTimesheetEntry";
import {
  type SubmitTimesheetFormValues,
  useTimesheetSubmission,
} from "../../Timekeeping/Timesheet/hooks/useTimesheetSubmission";
import { calculateAutoBreakTimes } from "../../Timekeeping/Timesheet/utils/calculateAutoBreakTimes";
import { calculateBreakDuration } from "../../Timekeeping/Timesheet/utils/calculateBreakDuration";
import { formatBreakDisplay } from "../../Timekeeping/Timesheet/utils/formatBreakDisplay";
import { isMandatoryBreakRequired } from "../../Timekeeping/Timesheet/utils/isMandatoryBreakRequired";
import { TimesheetSubmittedDialog } from "../../Timekeeping/TimesheetSubmittedDialog/TimesheetSubmittedDialog";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { ClipboardScoreBottomSheet } from "../components/ClipboardScoreBottomSheet";
import { EarningsSuccessBottomSheet } from "../components/EarningsSuccessBottomSheet";
import { useClipboardScoreData } from "../components/hooks/useClipboardScoreData";
import { calculateClipboardScoreChange } from "../components/utils/calculateClipboardScoreChange";
import { useTimesheetStep } from "./hooks/useTimesheetStep";
import { SubmitTimesheetPageSkeleton } from "./SubmitTimesheetPageSkeleton";

interface RouteParams {
  shiftId: string;
}

const ERROR_MESSAGES = {
  INTERNAL_TIMECLOCK_DISABLED:
    "Timestamps provided from an internal time clock cannot be modified.",
  TIMESHEET_EDITING_DISABLED: "Timesheet editing is currently disabled for this facility.",
} as const;

const MODAL_TRANSITION_DELAY_IN_MS = 50;

// eslint-disable-next-line complexity
export function SubmitTimesheetPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const { url: currentUrl } = useRouteMatch();

  // Strip the /submit-timesheet suffix to get the shift details base URL
  const shiftDetailsBaseUrl = currentUrl.endsWith(SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH)
    ? currentUrl.slice(0, -SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH.length)
    : currentUrl;

  useLogEffect(
    APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_PAGE_VIEWED,
    { shiftId },
    { enabled: isDefined(shiftId) }
  );

  const worker = useDefinedWorker();
  const { firstName, lastName, userId: workerId, tmz } = worker;
  const { showErrorToast } = useToast();

  const timesheetSubmittedModalState = useModalState();
  const earningsModalState = useModalState();
  const attendanceScoreModalState = useModalState();
  const restrictedScreenModalState = useModalState();

  const postShiftReviewModalState = useModalState();

  const delayedReviewConfig = useCbhFlag(CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW],
  });
  const isDelayedWorkplaceReviewEnabled = delayedReviewConfig.enabled;
  const { setDelayedReview } = useDelayedReviewStorage();
  const { isEligibleForECFlow } = useEmergencyCreditsClockOutFlow();

  // why don't we just history.push() to PayoutSettings?
  // because we want to keep SubmitTimesheetPage mounted and be able to
  // return to it and continue to Clipboard score etc.
  //
  // that's why we open PayoutSettings in a FullScreenDialog with modalState
  const payoutSettingsModalState = useModalState();

  const workedThroughMandatoryBreakExplanationModalState = useModalState();
  const incompleteBreakConfirmationModalState = useModalState();

  const {
    shift,
    shiftState,
    shiftError,
    isLoading: isShiftDataLoading,
    isShiftDataFetching,
    breakPolicyNoteId,
    refetchShift,
  } = useGetShiftDetailsData({
    shiftId: shiftId ?? "",
  });

  const { data: facilityNotesData } = useFacilityNotes(shift?.facilityId ?? "", {
    enabled: isDefined(shift?.facilityId) && isDefined(breakPolicyNoteId),
  });

  const breakPolicyNote = facilityNotesData?.find((note) => note.noteId === breakPolicyNoteId);

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shift?.facility?.userId,
  });

  const isTimekeepingIntegrationsEnabled = isDefined(
    useIsTimekeepingIntegrationsEnabled({
      config: shiftState?.metadata.externalTimekeepingIntegrationsConfig,
    })
  );

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(
    workerId,
    {
      enabled: isDefined(workerId) && isDefined(shift?._id),
    },
    shift?.agentReq
  );
  const {
    data: clipboardScoreHistory,
    refetch: refetchClipboardScoreHistory,
    isFetching: isClipboardScoreHistoryLoading,
  } = useGetClipboardScoreHistory(
    {
      userId: workerId,
      filter: isDefined(shift?._id) ? { shiftIds: [shift._id] } : undefined,
    },
    { enabled: isDefined(workerId) && isDefined(shift?._id) }
  );
  const { discardLatenessBooktimeThresholdInHours } = useDiscardLatenessBooktimeThreshold();

  const isTimesheetEditDisabled = shiftState?.metadata.isTimeSheetEditDisabled ?? false;

  const { useExpectedArrivalTime } = useArrivalTimeConfig();

  const { signOutWithQrCode, shiftFeedbackGiver } = useGetShiftFeedbackClockOutConfig(
    shift?.facilityId ?? "",
    {
      enabled: isDefined(shift?.facilityId),
    }
  );

  const { data: payoutDetails } = useGetPayoutSettings({ includeExternalAccount: true });
  const payoutDetailsCached = useRef<UseGetPayoutSettingsResponse | undefined>(undefined);

  // we cache the initial value of payoutDetails
  // this way, if the user updates something in Payout Settings, it won't reflect in payoutDetailsCached
  // Payout Settings is accessible in EarningsSuccessBottomSheet if user was on Standard payouts
  //
  // preserving first value is important because the payout already went out and we don't want to mislead the user
  // e.g. if they change to InstantPay, this will apply to their NEXT shift, not this one!
  if (isDefined(payoutDetails) && isNil(payoutDetailsCached.current)) {
    payoutDetailsCached.current = payoutDetails;
  }

  const { data: workplace } = useGetWorkplace(
    { workplaceId: shift?.facilityId ?? "" },
    { enabled: isDefined(shift?.facilityId) }
  );

  const ten99PolicyEntityId = workplace?.data?.attributes?.ten99PolicyEntityId;

  const { data: ten99PolicyAssignment, refetch: refetchTen99PolicyAssignment } =
    useGetAssignmentByShiftId(shiftId, { enabled: isDefined(ten99PolicyEntityId) });

  const ten99PolicyFee = getValueInUsd(
    ten99PolicyAssignment?.data?.attributes?.assignmentFeeInCents ?? 0
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const facilitySignatureSectionRef = useRef<FacilitySignatureSectionRef>(null);
  const editClockInModalState = useModalState();
  const editClockOutModalState = useModalState();
  const editBreakModalState = useModalState();

  const formTimesheet = useTimesheetEntry(shift);

  const isCaliforniaFacility = shift?.facility?.featureSettings?.californiaTimesheet ?? false;
  const workplaceTimezone = shift?.facility?.tmz ?? DEFAULT_TIMEZONE;
  const isQrCodeSubmission = signOutWithQrCode;

  const isNewTimeSheetEnabled = shift?.facility?.featureSettings?.displayTimeSheetSummary ?? true;
  const isDigitalSignatureEnabled = shift?.facility?.featureSettings?.digitalSignature ?? true;
  const isDigitalTimesheetEnabled = isNewTimeSheetEnabled && isDigitalSignatureEnabled;
  const { currentStep, setCurrentStep } = useTimesheetStep({
    isQrCodeSubmission,
    isDigitalTimesheetEnabled,
  });

  // used when the requested break change is LESS than the mandatoryBreakDurationInMinutes
  // worker must go through WorkedThroughMandatoryBreakExplanationDialog and provide a reason for working through the break
  // after which this pending break will be applied to the form via applyBreakChange()
  const pendingBreakChange = useRef<
    { breakStartTime: Date | undefined; breakDuration: Duration } | undefined
  >(undefined);

  const {
    handleSubmit: handleTimesheetSubmit,
    isLoading: isSubmissionLoading,
    formSchema,
  } = useTimesheetSubmission({
    shift,
    isCaliforniaFacility,
    currentStep,
    isQrCodeSubmission,
    onSuccess: async () => {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMITTED, {
        shiftId: shiftId ?? "",
        isCaliforniaFacility,
        step: currentStep,
      });
      await refetchShift();
      if (isDefined(ten99PolicyEntityId)) {
        await refetchTen99PolicyAssignment();
      }

      // Save the pending delayed review to local storage
      // This will be shown later (120+ minutes after shift end) when the user
      // next opens the app on a main navigation page and is not in an active shift
      if (isDelayedWorkplaceReviewEnabled && isDefined(shift) && isDefined(shift.clockInOut?.end)) {
        setDelayedReview({
          shiftId,
          facilityId: shift.facilityId,
          facilityName: shift.facility.name,
          facilityType: shift.facility.type ?? "Unknown",
          shiftEndTime: shift.clockInOut.end,
        });
      }

      if (isCaliforniaFacility || isQrCodeSubmission) {
        earningsModalState.openModal();
      } else {
        timesheetSubmittedModalState.openModal();
      }
    },
    onError: (errorMessage) => {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_FAILED, {
        shiftId: shiftId ?? "",
        isCaliforniaFacility,
        step: currentStep,
        errorMessage,
      });
      showErrorToast(errorMessage);
    },
  });

  const isTimesheetSubmissionLoading = isSubmissionLoading || isShiftDataFetching;

  /**
   * Skips the immediate workplace review prompt after timesheet submission.
   * The pending review is already saved,
   * so it will be shown later when the user opens the app on a main navigation page
   * and all eligibility conditions are met (120+ minutes after shift end, not in active shift).
   */
  const handleSkipImmediateWorkplaceReview = useCallback(() => {
    history.replace(MY_SHIFTS_FULL_PATH);
  }, [history]);

  const { control, handleSubmit, setValue, watch, formState, trigger } =
    useForm<SubmitTimesheetFormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        facilityEmployeeName: "",
        facilityEmployeeSignature: "",
        workerStation: "",
        workerNursingAssignment: "",
        clockInTime: new Date(),
        clockOutTime: new Date(),
        breakStartTime: undefined,
        breakEndTime: undefined,
        breakAuthorizationReason: undefined,
        // California HCP fields
        hcpName: `${firstName ?? ""} ${lastName ?? ""}`.trim(),
        hcpSignature: "",
      },
      mode: "onChange",
    });

  const { isValid } = formState;

  // Re-validate form when step changes to update isValid with new schema
  useEffect(() => {
    void trigger();
  }, [currentStep, trigger]);

  const formClockInTime = watch("clockInTime");
  const formClockOutTime = watch("clockOutTime");
  const formBreakStartTime = watch("breakStartTime");
  const formBreakEndTime = watch("breakEndTime");
  const formBreakAuthorizationReason = watch("breakAuthorizationReason");

  const [
    didFacilityEmployeeAcknowledgeBreakAuthorizationReason,
    setDidFacilityEmployeeAcknowledgeBreakAuthorizationReason,
  ] = useState(false);

  const formInitialized = useRef(false);

  const isMandatoryBreakRequiredForShift = useMemo(
    () =>
      isDefined(shift) && isDefined(shiftState) && shift.clockInOut?.start && shift.clockInOut?.end
        ? isMandatoryBreakRequired({
            requiresLunchBreak: shift.facility.requiresLunchBreak ?? false,
            solveUnpaidBreaksRolloutStageWithTargetingConfig:
              shiftState.metadata.solveUnpaidBreaksRolloutStageWithTargetingConfig,
            clockInTime: parseISO(shift.clockInOut.start),
            clockOutTime: parseISO(shift.clockInOut.end),
          })
        : false,
    [shift, shiftState]
  );

  const isMandatoryBreakRequiredForCurrentFormValues = useMemo(() => {
    if (!isDefined(shift) || !isDefined(shiftState) || !formClockInTime || !formClockOutTime) {
      return false;
    }

    return isMandatoryBreakRequired({
      requiresLunchBreak: shift.facility.requiresLunchBreak ?? false,
      solveUnpaidBreaksRolloutStageWithTargetingConfig:
        shiftState.metadata.solveUnpaidBreaksRolloutStageWithTargetingConfig,
      clockInTime: formClockInTime,
      clockOutTime: formClockOutTime,
    });
  }, [formClockInTime, formClockOutTime, shift, shiftState]);

  const initializeTimesheetFormWithData = useCallback(
    (timesheet: TimesheetEntry, isMandatoryBreakRequiredForShift: boolean) => {
      setValue("clockInTime", timesheet.clockInTime);
      setValue("clockOutTime", timesheet.clockOutTime);

      // Use existing break times if available
      if (isDefined(timesheet.breakStartTime) && isDefined(timesheet.breakEndTime)) {
        setValue("breakStartTime", timesheet.breakStartTime);
        setValue("breakEndTime", timesheet.breakEndTime);
        return;
      }

      // Otherwise, check if we should auto-add mandatory break
      if (!isMandatoryBreakRequiredForShift) {
        return;
      }

      // Auto-add break at the midpoint of the shift
      const { autoBreakStartTime, autoBreakEndTime } = calculateAutoBreakTimes(
        timesheet.clockInTime,
        timesheet.clockOutTime,
        mandatoryBreakDurationInMinutes
      );
      setValue("breakStartTime", autoBreakStartTime);
      setValue("breakEndTime", autoBreakEndTime);
    },
    [setValue, mandatoryBreakDurationInMinutes]
  );

  // Initialize form with timesheet data when available (only once)
  useEffect(() => {
    if (
      !formInitialized.current &&
      isDefined(formTimesheet) &&
      isDefined(isMandatoryBreakRequiredForShift)
    ) {
      initializeTimesheetFormWithData(formTimesheet, isMandatoryBreakRequiredForShift);
      formInitialized.current = true;
    }
  }, [formTimesheet, isMandatoryBreakRequiredForShift, initializeTimesheetFormWithData]);

  const displayTimesheet = isDefined(formTimesheet)
    ? {
        clockInTime: formClockInTime,
        clockOutTime: formClockOutTime,
        breakStartTime: formBreakStartTime,
        breakEndTime: formBreakEndTime,
      }
    : undefined;

  const breakDisplayText = formatBreakDisplay({
    displayTimesheet,
    workplaceTimezone,
  });

  function applyBreakChange(newBreakStartTime: Date | undefined, newBreakDuration: Duration) {
    setValue("breakStartTime", newBreakStartTime, { shouldValidate: true });

    // Calculate break end time from start time and duration
    if (newBreakDuration.hours === 0 && newBreakDuration.minutes === 0) {
      // Break is skipped
      setValue("breakEndTime", undefined, { shouldValidate: true });
    } else if (newBreakStartTime) {
      // Calculate end time from start time + duration
      let endTime = new Date(newBreakStartTime);
      if (newBreakDuration.hours > 0) {
        endTime = addHours(endTime, newBreakDuration.hours);
      }

      if (newBreakDuration.minutes > 0) {
        endTime = addMinutes(endTime, newBreakDuration.minutes);
      }

      setValue("breakEndTime", endTime, { shouldValidate: true });
    } else {
      // No start time, so no end time
      setValue("breakEndTime", undefined, { shouldValidate: true });
    }

    // Clear facility signature when break times are modified
    facilitySignatureSectionRef.current?.clearSignature();
  }

  function showEditingDisabledErrorToast(message: string) {
    showErrorToast(message);
  }

  function getTimesheetHeaderText() {
    if (isTimekeepingIntegrationsEnabled) {
      return "The following times have been provided by the facility's internal time clock and cannot be changed here.";
    }

    if (isTimesheetEditDisabled) {
      return "Submit your timesheet now to get paid instantly.";
    }

    return "Make sure the times are correct and make any edits before getting it signed.";
  }

  // Determine if editing is allowed (during facility signing and QR code steps, not during HCP signing)
  // Also disabled if the feature flag is enabled or if timekeeping integrations are enabled
  const isEditingAllowed = currentStep === "facility-signing" || currentStep === "qr-code";

  const instantPayTotalAmount = shift?.instantPayDetails?.totalAmount;

  useLogEffect(
    APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_INSTANT_PAY_EARNINGS_NOT_AVAILABLE,
    {
      shiftId,
      instantPayDetails: shift?.instantPayDetails,
      shiftTime: shift?.time,
      finalPay: shift?.finalPay,
      clockInOut: shift?.clockInOut,
    },
    {
      enabled: !isDefined(instantPayTotalAmount) && earningsModalState.modalIsOpen,
    }
  );

  const {
    attendanceScoreData,
    ranking,
    isFirstShiftInDouble,
    isSecondShiftInDouble,
    wasLastMinuteBooking,
    latenessMinutes,
    isAlreadyRestricted,
    newlyRestricted,
    isScoreFrozen,
  } = useClipboardScoreData({
    clipboardScoreHistory,
    clipboardScoreProfile,
    shift,
    discardLatenessBooktimeThresholdInHours,
  });

  const reEnrollmentDate = clipboardScoreProfile?.accountStatus?.reEnrollmentDate;
  const reEnrollmentScore = clipboardScoreProfile?.restriction?.reEnrollmentScore ?? 80;

  const isFinalRestriction =
    clipboardScoreProfile?.accountStatus.restrictionDuration ===
    clipboardScoreProfile?.policy?.finalRestriction?.duration;

  const formattedTargetArrivalTime = isDefined(shift?.computedArrival?.targetArrivalTime)
    ? formatTime(parseISO(shift.computedArrival.targetArrivalTime), workplaceTimezone)
    : undefined;

  const areTimeEntriesReadOnly =
    (isCaliforniaFacility && currentStep === "hcp-signing") || currentStep === "timesheet-upload";

  const isPayoutInstant = Boolean(
    payoutDetailsCached.current?.attributes.payoutSchedule === "instant" &&
    payoutDetailsCached.current?.meta.payoutCapability === "instant"
  );
  const payoutFee = isPayoutInstant ? (payoutDetailsCached.current?.meta.instantPayoutFee ?? 0) : 0;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary, paddingX: 0 }}
      >
        <StickyScrollPageHeader
          title="Your timesheet"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
        />

        <PageContent ref={scrollRef} sx={{ paddingX: 0 }}>
          {isShiftDataLoading && <SubmitTimesheetPageSkeleton />}

          {shiftError ? (
            <Alert severity="error" sx={{ mt: 4 }}>
              Something went wrong while loading this shift. Please try again.
            </Alert>
          ) : undefined}

          {!isShiftDataLoading && !shiftError && shift && displayTimesheet && (
            <form>
              <Stack spacing={6} sx={{ paddingX: 6, paddingBottom: 6 }}>
                {/* Time Entries - Different component based on step */}
                {areTimeEntriesReadOnly ? (
                  <ReadOnlyTimesheetTimeEntries
                    displayTimesheet={displayTimesheet}
                    workplaceTimezone={workplaceTimezone}
                    formatBreakDisplay={() => breakDisplayText}
                  />
                ) : (
                  <>
                    {!isTimekeepingIntegrationsEnabled &&
                      !isTimesheetEditDisabled &&
                      isMandatoryBreakRequiredForCurrentFormValues && (
                        <Alert severity="info" sx={{ mb: 4 }}>
                          This facility requires you to take a {mandatoryBreakDurationInMinutes}{" "}
                          minute break if you work more than{" "}
                          {MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS} hours. If you didn&apos;t take
                          a break, please edit the time below.
                        </Alert>
                      )}
                    {/* Header */}
                    <Text variant="body1" sx={{ color: (theme) => theme.palette.text.secondary }}>
                      {getTimesheetHeaderText()}
                    </Text>

                    <TimesheetTimeEntries
                      hidePencilIcons={isTimekeepingIntegrationsEnabled || isTimesheetEditDisabled}
                      displayTimesheet={displayTimesheet}
                      workplaceTimezone={workplaceTimezone}
                      formatBreakDisplay={() => breakDisplayText}
                      onEditBreak={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_BREAK_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editBreakModalState.openModal();
                        }
                      }}
                      onEditClockIn={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_CLOCK_IN_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editClockInModalState.openModal();
                        }
                      }}
                      onEditClockOut={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_CLOCK_OUT_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editClockOutModalState.openModal();
                        }
                      }}
                    />
                  </>
                )}
              </Stack>

              {currentStep === "qr-code" && (
                <QrSignatureSection
                  workerFirstName={firstName ?? ""}
                  isCaliforniaTimesheet={isCaliforniaFacility}
                  control={control}
                  shiftFeedbackGiver={shiftFeedbackGiver}
                  shiftId={shiftId}
                  facilityId={shift.facilityId}
                  workerId={workerId}
                  setValue={setValue}
                  handleSubmit={handleSubmit(async (data: SubmitTimesheetFormValues) => {
                    await handleTimesheetSubmit(data);
                  })}
                  setCurrentStep={(step) => {
                    setCurrentStep(step, { addToHistory: false });
                  }}
                />
              )}

              {currentStep === "facility-signing" && (
                <FacilitySignatureSection
                  ref={facilitySignatureSectionRef}
                  control={control}
                  firstName={firstName ?? ""}
                  isCaliforniaFacility={isCaliforniaFacility}
                  signaturePadIsDisabled={isTimesheetSubmissionLoading}
                  isValid={isValid}
                  setValue={setValue}
                  onDisabledSignaturePadTouchStart={() => {
                    const shouldShowIncompleteBreakConfirmation =
                      isDefined(formBreakAuthorizationReason) &&
                      formBreakAuthorizationReason.length > 0 &&
                      !didFacilityEmployeeAcknowledgeBreakAuthorizationReason;

                    if (shouldShowIncompleteBreakConfirmation) {
                      incompleteBreakConfirmationModalState.openModal();
                    }
                  }}
                  onSubmit={handleSubmit(async (data: SubmitTimesheetFormValues) => {
                    if (isCaliforniaFacility) {
                      // For California: Show "pass phone back" dialog first
                      timesheetSubmittedModalState.openModal();
                    } else {
                      // Non-California: submit directly
                      await handleTimesheetSubmit(data);
                    }
                  })}
                />
              )}

              {/* Signature Section - Different component based on step */}
              {isCaliforniaFacility && currentStep === "hcp-signing" && (
                <WorkerSignatureSection
                  control={control}
                  firstName={firstName ?? ""}
                  fullName={`${firstName ?? ""} ${lastName ?? ""}`}
                  workerStation={watch("workerStation") ?? ""}
                  workerNursingAssignment={watch("workerNursingAssignment") ?? ""}
                  signaturePadIsDisabled={isTimesheetSubmissionLoading}
                  setValue={setValue}
                >
                  <LoadingButton
                    fullWidth
                    type="submit"
                    size="large"
                    variant="contained"
                    disabled={!isValid}
                    isLoading={isTimesheetSubmissionLoading}
                    sx={{
                      mt: 6,
                      mb: 6,
                      "&.Mui-disabled": {
                        backgroundColor: (theme) => theme.palette.grey[300],
                        color: (theme) => theme.palette.grey[500],
                      },
                    }}
                    onClick={handleSubmit(handleTimesheetSubmit)}
                  >
                    Submit
                  </LoadingButton>
                </WorkerSignatureSection>
              )}

              {currentStep === "timesheet-upload" && (
                <TimesheetUploadSection
                  is100InstantPayEnabled={
                    isDefined(shift.instantPayDetails?.is100InstantPayEnabled) &&
                    shift.instantPayDetails.is100InstantPayEnabled
                  }
                  shiftId={shift._id}
                  agentId={shift.agentId}
                  agentName={shift.agent?.name ?? shift.agentId}
                  facilityId={shift.facilityId}
                  isTimesheetUploaded={(shift.timecard?.files.length ?? 0) > 0}
                  timesheetExists={
                    (shift.timecard?.files.length ?? 0) > 0 ||
                    isDefined(shift.timecardNotAvailable?.reason)
                  }
                  onSuccess={async () => {
                    earningsModalState.openModal();
                  }}
                  onSkip={async () => {
                    earningsModalState.openModal();
                  }}
                />
              )}
            </form>
          )}
        </PageContent>

        {/* Edit Modals - Only available during facility signing step */}
        {isEditingAllowed &&
          isDefined(shift) &&
          isDefined(shiftState) &&
          isDefined(displayTimesheet) && (
            <EditClockInTimeBottomSheet
              modalState={editClockInModalState}
              workplaceTimezone={workplaceTimezone}
              shiftStartAt={parseISO(shift.start)}
              shiftEndAt={parseISO(shift.end)}
              isNfcShift={isDefined(shift.nfcTag) && shift.nfcTag}
              earliestGeofenceEntryTime={
                shift.geofence?.entry ? parseISO(shift.geofence.entry) : undefined
              }
              earliestPlausibleArrivalTime={
                shift.attendance?.earliestPlausibleArrivalAt
                  ? parseISO(shift.attendance.earliestPlausibleArrivalAt)
                  : undefined
              }
              clockInButtonTappedAt={formTimesheet?.clockInTime}
              earlyClockInEnabled={shiftState.metadata.isEarlyClockInEnabled ?? false}
              earlyClockInEnabledInMinutes={shiftState.metadata.earlyClockInEnabledInMinutes}
              clockInTime={formClockInTime}
              clockOutTime={formClockOutTime}
              breakStartTime={formBreakStartTime}
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              isEnforceGeofenceTimestampsEnabled={
                shiftState.metadata.isEnforceGeofenceTimestampsEnabled
              }
              shiftId={shiftId}
              workplaceId={shift.facility?.userId}
              onConfirm={(newClockInTime) => {
                setValue("clockInTime", newClockInTime, { shouldValidate: true });
                facilitySignatureSectionRef.current?.clearSignature();
              }}
            />
          )}

        {isEditingAllowed &&
          isDefined(shift) &&
          isDefined(shiftState) &&
          isDefined(displayTimesheet) && (
            <EditClockOutTimeBottomSheet
              modalState={editClockOutModalState}
              clockOutTime={formClockOutTime}
              workplaceTimezone={workplaceTimezone}
              clockOutButtonTappedAt={formTimesheet?.clockOutTime}
              breakEndTime={formBreakEndTime}
              clockInTime={formClockInTime}
              shiftStartAt={parseISO(shift.start)}
              shiftEndAt={parseISO(shift.end)}
              latestPlausibleDepartureTime={
                shift.attendance?.latestPlausibleDepartureAt
                  ? parseISO(shift.attendance.latestPlausibleDepartureAt)
                  : undefined
              }
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              isEnforceGeofenceTimestampsEnabled={
                shiftState.metadata.isEnforceGeofenceTimestampsEnabled
              }
              shiftId={shiftId}
              workplaceId={shift.facility?.userId}
              onConfirm={(newClockOutTime) => {
                setValue("clockOutTime", newClockOutTime, { shouldValidate: true });
                facilitySignatureSectionRef.current?.clearSignature();
              }}
            />
          )}

        {isEditingAllowed &&
          isDefined(shift) &&
          isDefined(shiftState) &&
          isDefined(displayTimesheet) && (
            <EditBreakTimeBottomSheet
              modalState={editBreakModalState}
              shiftStartAt={parseISO(shift.start)}
              shiftEndAt={parseISO(shift.end)}
              breakStartTime={formBreakStartTime}
              defaultBreakDuration={minutesToDuration(mandatoryBreakDurationInMinutes)}
              breakDuration={calculateBreakDuration(formBreakStartTime, formBreakEndTime)}
              initialBreakSkipped={!isDefined(formBreakStartTime)}
              workplaceTimezone={workplaceTimezone}
              clockInTime={formClockInTime}
              clockOutTime={formClockOutTime}
              isShiftVerified={shift.verified ?? false}
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              shiftId={shiftId}
              workplaceId={shift.facility?.userId}
              onConfirm={(newBreakStartTime, newBreakDuration) => {
                if (
                  isMandatoryBreakRequiredForCurrentFormValues &&
                  durationToMinutes(newBreakDuration) < mandatoryBreakDurationInMinutes
                ) {
                  workedThroughMandatoryBreakExplanationModalState.openModal();
                  pendingBreakChange.current = {
                    breakStartTime: newBreakStartTime,
                    breakDuration: newBreakDuration,
                  };
                } else {
                  setValue("breakAuthorizationReason", undefined);
                  applyBreakChange(newBreakStartTime, newBreakDuration);
                }
              }}
            />
          )}

        <TimesheetSubmittedDialog
          modalState={timesheetSubmittedModalState}
          isCaliforniaFacility={isCaliforniaFacility}
          onContinue={async () => {
            if (isCaliforniaFacility) {
              timesheetSubmittedModalState.closeModal();
              setCurrentStep("hcp-signing");
            } else {
              earningsModalState.openModal();

              // Close the previous modal with a little delay to prevent the underlying page from being visible during the transition
              setTimeout(() => {
                timesheetSubmittedModalState.closeModal();
              }, MODAL_TRANSITION_DELAY_IN_MS);
            }
          }}
        />
        {isDefined(shift) && isMandatoryBreakRequiredForCurrentFormValues ? (
          <WorkedThroughMandatoryBreakExplanationDialog
            modalState={workedThroughMandatoryBreakExplanationModalState}
            breakPolicy={breakPolicyNote?.note ?? ""}
            workplaceId={shift.facilityId}
            workplaceName={shift.facility.name}
            mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
            onSuccess={(explanation) => {
              setValue("breakAuthorizationReason", explanation);

              if (pendingBreakChange.current) {
                applyBreakChange(
                  pendingBreakChange.current.breakStartTime,
                  pendingBreakChange.current.breakDuration
                );
                pendingBreakChange.current = undefined;
              }

              workedThroughMandatoryBreakExplanationModalState.closeModal();
            }}
          />
        ) : null}
        {formBreakAuthorizationReason ? (
          <IncompleteBreakConfirmationDialog
            modalState={incompleteBreakConfirmationModalState}
            professionalStatement={formBreakAuthorizationReason}
            onContinue={() => {
              setDidFacilityEmployeeAcknowledgeBreakAuthorizationReason(true);
              incompleteBreakConfirmationModalState.closeModal();
            }}
          />
        ) : null}

        {isDefined(shift) && (
          <EarningsSuccessBottomSheet
            modalState={earningsModalState}
            isAfterAnotherShift={shift.isAfterAnotherShift ?? false}
            grossEarnings={instantPayTotalAmount}
            hourlyRate={shift?.finalPay}
            isPayoutInstant={isPayoutInstant}
            payoutDestination={payoutDetailsCached.current?.destinationDetails}
            shiftId={shiftId}
            payoutFee={payoutFee}
            ten99PolicyFee={ten99PolicyFee}
            continueButtonIsLoading={isClipboardScoreHistoryLoading}
            onOpenPayoutSettings={payoutSettingsModalState.openModal}
            onContinue={async () => {
              // Only show attendance score modal if user is not restricted
              if (isAlreadyRestricted) {
                logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_POST_SHIFT_REVIEW_MODAL_OPENED, {
                  reason: "user_already_restricted",
                });
                if (isDelayedWorkplaceReviewEnabled) {
                  handleSkipImmediateWorkplaceReview();
                } else {
                  postShiftReviewModalState.openModal();
                }
              } else {
                // refetch score logs to get the post-clock out updates
                const updatedScoreHistory = await refetchClipboardScoreHistory();
                const updatedAttendanceScoreData = calculateClipboardScoreChange(
                  updatedScoreHistory.data
                );

                if (isDefined(updatedAttendanceScoreData) && !isScoreFrozen) {
                  // Redirect to Emergency Credits clock-out flow if worker is eligible
                  // EC flow will display the score change internally (MX-5864)
                  if (isEligibleForECFlow) {
                    const ecClockOutFlowPath = `${shiftDetailsBaseUrl}${SHIFT_DETAILS_EC_CLOCK_OUT_FLOW_PATH}`;
                    history.replace(ecClockOutFlowPath, {
                      facility: {
                        id: shift.facilityId,
                        name: shift.facility.name,
                        type: shift.facility.type ?? "Unknown",
                      },
                      isDelayedWorkplaceReviewEnabled,
                      scoreLogs: updatedScoreHistory.data?.scoreLogs ?? [],
                      currentScore: updatedAttendanceScoreData.currentScore,
                      scoreChange: updatedAttendanceScoreData.scoreChange,
                      latenessMinutes,
                      isSecondShiftInDouble,
                      isFirstShiftInDouble,
                      wasBookedWithShortLeadTime: wasLastMinuteBooking,
                      newlyRestricted,
                      isArrivalTimeFeatureEnabled: useExpectedArrivalTime,
                      formattedTargetArrivalTime,
                    });
                    return;
                  }

                  logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_ATTENDANCE_SCORE_MODAL_OPENED, {
                    reason: "attendance_score_data_available",
                    currentScore: updatedAttendanceScoreData.currentScore,
                    scoreChange: updatedAttendanceScoreData.scoreChange,
                  });
                  attendanceScoreModalState.openModal();
                } else {
                  logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_POST_SHIFT_REVIEW_MODAL_OPENED, {
                    reason: isScoreFrozen ? "frozen_clipboard_score" : "no_attendance_score_data",
                  });
                  if (isDelayedWorkplaceReviewEnabled) {
                    handleSkipImmediateWorkplaceReview();
                  } else {
                    postShiftReviewModalState.openModal();
                  }
                }
              }

              // Close the previous modal with a little delay to prevent the underlying page from being visible during the transition
              setTimeout(() => {
                earningsModalState.closeModal();
              }, MODAL_TRANSITION_DELAY_IN_MS);
            }}
          />
        )}

        {isDefined(shift) && (
          <PostShiftReviewFlow
            modalState={postShiftReviewModalState}
            facility={{
              id: shift.facilityId,
              name: shift.facility.name,
              type: shift.facility.type ?? "Unknown",
              // Note: facility photo not available in shift data
              photo: undefined,
            }}
            shiftId={shiftId}
            onComplete={() => {
              postShiftReviewModalState.closeModal();
              history.replace(MY_SHIFTS_FULL_PATH);
            }}
          />
        )}

        {isDefined(shift) && isDefined(attendanceScoreData) && !isScoreFrozen && (
          <ClipboardScoreBottomSheet
            modalState={attendanceScoreModalState}
            currentScore={attendanceScoreData.currentScore}
            scoreChange={attendanceScoreData.scoreChange}
            scoreChangeReasons={attendanceScoreData.reasons}
            ranking={ranking}
            shiftId={shiftId ?? ""}
            isFirstShiftInDouble={isFirstShiftInDouble}
            isSecondShiftInDouble={isSecondShiftInDouble}
            isAlreadyRestricted={isAlreadyRestricted}
            newlyRestricted={newlyRestricted}
            latenessMinutes={latenessMinutes}
            wasLastMinuteBooking={wasLastMinuteBooking}
            isArrivalTimeFeatureEnabled={useExpectedArrivalTime}
            formattedTargetArrivalTime={formattedTargetArrivalTime}
            onContinue={() => {
              if (newlyRestricted) {
                restrictedScreenModalState.openModal();
              } else if (isDelayedWorkplaceReviewEnabled) {
                handleSkipImmediateWorkplaceReview();
              } else {
                postShiftReviewModalState.openModal();
              }

              // Close the previous modal with a little delay to prevent the underlying page from being visible during the transition
              setTimeout(() => {
                attendanceScoreModalState.closeModal();
              }, MODAL_TRANSITION_DELAY_IN_MS);
            }}
          />
        )}
        {isDefined(shift) && isDefined(attendanceScoreData) && newlyRestricted && (
          <RestrictedBottomSheet
            modalState={restrictedScreenModalState}
            isFinalRestriction={isFinalRestriction}
            liftTime={
              reEnrollmentDate && formatInTimeZone(parseISO(reEnrollmentDate), tmz, "MMMM d, yyyy")
            }
            reEnrollmentScore={reEnrollmentScore}
            onContinue={() => {
              if (isDelayedWorkplaceReviewEnabled) {
                handleSkipImmediateWorkplaceReview();
              } else {
                postShiftReviewModalState.openModal();
              }

              // Close the previous modal with a little delay to prevent the underlying page from being visible during the transition
              setTimeout(() => {
                restrictedScreenModalState.closeModal();
              }, MODAL_TRANSITION_DELAY_IN_MS);
            }}
          />
        )}
        <FullScreenDialog modalState={payoutSettingsModalState}>
          <PayoutSettingsPage onClickBack={payoutSettingsModalState.closeModal} />
        </FullScreenDialog>
      </PageWrapper>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
